import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { SweetAlertService } from 'src/app/services/sweet-alert.service';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';
import { saveLoginPrefillFromLanding } from '../login-prefill.storage';

export interface LandingBienvenidaBenefit {
  titulo: string;
  texto: string;
  icon: string;
}

export interface LandingBienvenidaSession {
  primerNombre?: string;
  primerApellido?: string;
  tipoPersona?: string;
  /** Tipo doc para login (idTipoDocIdentidad). */
  tipoDocumento?: string | number;
  /** Número doc para login (PN: documento; PJ: documento del representante). */
  numeroDocumento?: string;
}

const LANDING_SESSION_KEY = 'landing-bienvenida';

/** Copy HU / Figma PN — fallback si PAR-089 no trae JSON válido. */
const DEFAULT_BENEFITS: LandingBienvenidaBenefit[] = [
  {
    titulo: 'Ahorro de tiempo',
    texto:
      'Olvida las filas y las múltiples solicitudes: Los bancos compiten por tu negocio.',
    icon: 'assets/img/Home/valor/icon-benefit-time.svg',
  },
  {
    titulo: 'Mejores condiciones',
    texto: 'Aumentamos tus posibilidades de obtener tasas más favorables.',
    icon: 'assets/img/Home/valor/icon-benefit-conditions.svg',
  },
  {
    titulo: 'Simplicidad total',
    texto:
      'Una sola carga de documentos te abre las puertas de múltiples opciones de crédito.',
    icon: 'assets/img/Home/valor/icon-benefit-simplicity.svg',
  },
];

const IMAGE_NATURAL = 'assets/img/landing/landing-bienvenida-natural.png';
const IMAGE_NATURAL_MOBILE = 'assets/img/landing/landing-bienvenida-natural-mobile.png';
const IMAGE_JURIDICA = 'assets/img/landing/landing-bienvenida-juridica.png';
const IMAGE_JURIDICA_MOBILE = 'assets/img/landing/landing-bienvenida-juridica-mobile.png';

@Component({
  selector: 'app-landing-aceptacion-usuario',
  templateUrl: './landing-aceptacion-usuario.component.html',
  styleUrls: ['./landing-aceptacion-usuario.component.scss'],
  standalone: false,
})
export class LandingAceptacionUsuarioComponent implements OnInit {
  benefits: LandingBienvenidaBenefit[] = [...DEFAULT_BENEFITS];
  primerNombre = '';
  primerApellido = '';
  tipoPersona: 'natural' | 'juridica' = 'natural';

  constructor(
    private readonly _bancoldexService: BancoldexService,
    private readonly _sweetAlert: SweetAlertService,
    private readonly _router: Router,
    private readonly _analyticsTag: AnalyticsTagService
  ) {
    window.scroll(0, 0);
  }

  ngOnInit(): void {
    this.hydrateFromSession();
    this.getMensajeBienvenida();
  }

  get isJuridica(): boolean {
    return this.tipoPersona === 'juridica';
  }

  get displayName(): string {
    const name = `${this.primerNombre || ''} ${this.primerApellido || ''}`.trim();
    return name || 'emprendedor';
  }

  get portraitSrc(): string {
    return this.isJuridica ? IMAGE_JURIDICA : IMAGE_NATURAL;
  }

  get portraitSrcMobile(): string {
    return this.isJuridica ? IMAGE_JURIDICA_MOBILE : IMAGE_NATURAL_MOBILE;
  }

  /**
   * Persiste tipo + número documento solo para este origen (landing),
   * limpia sesión de bienvenida y navega a login (HU 8337 Fase 3).
   */
  goToLogin(): void {
    this._analyticsTag.trackCtaClick({
      ctaLabel: 'Inicia sesión',
      ctaSection: 'landing-bienvenida',
      ctaComponent: 'landing-aceptacion-usuario',
      ctaElementId: 'btn-landing-inicia-sesion',
      hu: '8335',
    });
    try {
      const raw = sessionStorage.getItem(LANDING_SESSION_KEY);
      if (raw) {
        const data = JSON.parse(raw) as LandingBienvenidaSession;
        saveLoginPrefillFromLanding(data?.tipoDocumento, data?.numeroDocumento);
      }
      sessionStorage.removeItem(LANDING_SESSION_KEY);
    } catch {
      // ignore
    }
    this._router.navigate(['/microempresario/login']);
  }

  private hydrateFromSession(): void {
    try {
      const raw = sessionStorage.getItem(LANDING_SESSION_KEY);
      if (!raw) {
        return;
      }
      const data = JSON.parse(raw) as LandingBienvenidaSession;
      this.primerNombre = (data.primerNombre || '').trim();
      this.primerApellido = (data.primerApellido || '').trim();
      const tipo = (data.tipoPersona || 'natural').toString().toLowerCase();
      this.tipoPersona =
        tipo === 'juridica' || tipo === 'juridico' || tipo === 'pj'
          ? 'juridica'
          : 'natural';
      // Mobile: CTA solo en header → precarga lista antes de navegar
      saveLoginPrefillFromLanding(data?.tipoDocumento, data?.numeroDocumento);
    } catch {
      // ignore
    }
  }

  getMensajeBienvenida(): void {
    const json = {
      idParametro: 'PAR-089',
      nombre: null,
      isParFuncional: true,
      pagina: 1,
    };
    this._bancoldexService.getFilterParametro(json).subscribe({
      next: (response: any) => {
        const valor = response?.data?.[0]?.valorParametro;
        if (typeof valor === 'string' && valor.trim()) {
          const parsed = this.tryParseStructuredBenefits(valor);
          if (parsed?.length) {
            this.benefits = parsed;
          }
        }
      },
      error: (error) => {
        this._sweetAlert.errorModal('Error!', true, error);
      },
    });
  }

  /**
   * Contrato PAR-089 (valor en claro tras desencriptar en back):
   * 1) JSON `{ "items": [{ "titulo", "texto" }] }` o array directo.
   * 2) HTML legacy con lista, p. ej.:
   *    <ol><li><strong>Título</strong> Texto...</li>...</ol>
   * Si no se puede parsear → defaults HU.
   */
  private tryParseStructuredBenefits(raw: string): LandingBienvenidaBenefit[] | null {
    const fromJson = this.tryParseBenefitsJson(raw);
    if (fromJson?.length) {
      return fromJson;
    }
    return this.tryParseBenefitsHtml(raw);
  }

  private tryParseBenefitsJson(raw: string): LandingBienvenidaBenefit[] | null {
    try {
      const parsed = JSON.parse(raw);
      const items = Array.isArray(parsed?.items)
        ? parsed.items
        : Array.isArray(parsed)
          ? parsed
          : null;
      if (!items?.length) {
        return null;
      }
      return items.slice(0, 3).map((item: any, index: number) => ({
        titulo: String(item?.titulo || DEFAULT_BENEFITS[index]?.titulo || ''),
        texto: String(item?.texto || DEFAULT_BENEFITS[index]?.texto || ''),
        icon: DEFAULT_BENEFITS[index]?.icon || DEFAULT_BENEFITS[0].icon,
      }));
    } catch {
      return null;
    }
  }

  private tryParseBenefitsHtml(raw: string): LandingBienvenidaBenefit[] | null {
    const html = (raw || '').trim();
    if (!html || !html.includes('<')) {
      return null;
    }
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = Array.from(doc.querySelectorAll('li'));
      if (!items.length) {
        return null;
      }
      return items.slice(0, 3).map((li, index) => {
        const strong = li.querySelector('strong, b');
        const titulo =
          (strong?.textContent || '').trim() ||
          DEFAULT_BENEFITS[index]?.titulo ||
          '';
        let texto = '';
        if (strong) {
          const clone = li.cloneNode(true) as HTMLElement;
          clone.querySelectorAll('strong, b').forEach((node) => node.remove());
          texto = (clone.textContent || '').replace(/\s+/g, ' ').trim();
        } else {
          texto = (li.textContent || '').replace(/\s+/g, ' ').trim();
        }
        return {
          titulo,
          texto: texto || DEFAULT_BENEFITS[index]?.texto || '',
          icon: DEFAULT_BENEFITS[index]?.icon || DEFAULT_BENEFITS[0].icon,
        };
      });
    } catch {
      return null;
    }
  }
}
